import { Coffee, Undo2, type LucideIcon } from 'lucide-react';
import type { HotelRateChip, HotelRateView } from '../models.js';
import type { HotelRoomsCopy } from './hotel_rooms.js';

/** Container único de toda tag impressa numa linha de benefícios — quem imprime
 *  outra tag NA MESMA linha usa este, senão a linha mistura duas alturas de tag
 *  (mesma regra do fare selector do design hub). */
export const CHIP =
  'inline-flex items-center gap-1 rounded-full px-1.5 leading-5 whitespace-nowrap';

/** Cor por estado, não forma de container: verde = incluído, vermelho light =
 *  ausente (nunca o escudo de política, que é sólido e mora na coluna do preço),
 *  neutro = fato sem julgamento (o prazo de cancelamento da linha). */
export const CHIP_TONE = {
  included: 'bg-(--success-soft) text-(--success-strong)',
  absent: 'bg-(--destructive)/10 text-(--destructive)',
  neutral: 'bg-muted text-muted-foreground',
} as const;

/** Forma e cor por VALOR do enum — o rótulo é o único pedaço que a copy traz,
 *  então nenhum chip pode aparecer com texto do fornecedor nem com tom que
 *  contradiga o dado. */
const CHIP_BY_VALUE: Record<HotelRateChip, { tone: keyof typeof CHIP_TONE; Icon: LucideIcon }> = {
  WITH_BREAKFAST: { tone: 'included', Icon: Coffee },
  WITHOUT_BREAKFAST: { tone: 'absent', Icon: Coffee },
  WITH_REFUND: { tone: 'included', Icon: Undo2 },
  WITHOUT_REFUND: { tone: 'absent', Icon: Undo2 },
};

function benefitChips(rate: HotelRateView): HotelRateChip[] {
  return [rate.breakfast, rate.refund].filter(
    (value): value is HotelRateChip => value !== undefined,
  );
}

/** Os chips do que a tarifa entrega (café · reembolso), na forma do fare
 *  selector. O card fechado e cada linha do painel imprimem os MESMOS chips —
 *  duas cópias divergiriam no primeiro ajuste de cor. */
export function RateBenefitChips({
  rate,
  copy,
}: {
  readonly rate: HotelRateView;
  readonly copy: HotelRoomsCopy;
}) {
  return (
    <>
      {benefitChips(rate).map((value) => {
        const { tone, Icon } = CHIP_BY_VALUE[value];
        return (
          <span key={value} className={`${CHIP} ${CHIP_TONE[tone]}`}>
            <Icon className="size-3 shrink-0" aria-hidden />
            {copy.rateChipLabels[value]}
          </span>
        );
      })}
    </>
  );
}

export function rateHasBenefitChips(rate: HotelRateView): boolean {
  return benefitChips(rate).length > 0;
}
